import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import {
  SettingType,
  fetchAdvancedSettings,
  postAdvancedSetting,
  resetStatus,
} from '../reducers/advanced.settings.reducer';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Editor from '@monaco-editor/react';
import Spacer from '../components/layout/Spacer';
import Loading from '../components/loading/Loading';
import { useToast } from '../components/toast/ToastProvider';
import { features } from 'process';
import ls from 'localstorage-slim';

const AdvancedSettings = () => {
  const params = useParams();
  const { id: shopId } = params;
  const dispatch = useAppDispatch();
  const {
    data,
    status,
    postCustomCssStatus,
    postCustomJsStatus,
    postWidgetCssStatus,
    postWidgetFeaturesStatus,
  } = useAppSelector((state) => state.advancedSettingsReducer);

  const toast = useToast();
  const [customCss, setCustomCss] = useState<string>('');
  const [customJs, setCustomJs] = useState<string>('');
  const [widgetCss, setWidgetCss] = useState<string>('');
  const [widgetFeatures, setWidgetFeatures] = useState<any>('');

  const [editorHeight, setEditorHeight] = useState(ls.get('editor_height') || 360)
  
  useEffect(() => {
    const results = [
      { status: postCustomCssStatus, success: "Successfully changed the CTA's css", error: "Failed to change the CTA's css" },
      { status: postCustomJsStatus, success: 'Successfully changed the custom js', error: 'Failed to change the custom js' },
      { status: postWidgetCssStatus, success: "Successfully changed widget's css", error: "Failed to change the widget's css" },
      { status: postWidgetFeaturesStatus, success: "Successfully changed the widget's feature", error: "Failed to change the widget's feature" },
    ];
    results.forEach(({ status, success, error }) => {
      if (status === 'successful') {
        toast.show(success);
      } else if (status === 'error') {
        toast.show(error, { isError: true });
      }
    });
    dispatch(resetStatus());
  }, [
    postCustomCssStatus,
    postCustomJsStatus,
    postWidgetCssStatus,
    postWidgetFeaturesStatus,
  ]);

  const handleSubmit = (settingType: SettingType) => {
    switch (settingType) {
      case 'customcss':
        dispatch(
          postAdvancedSetting({
            value: customCss,
            shopId: shopId!,
            settingType,
          })
        );
        break;
      case 'customjs':
        dispatch(
          postAdvancedSetting({ value: customJs, shopId: shopId!, settingType })
        );
        break;
      case 'widgetcss':
        dispatch(
          postAdvancedSetting({
            value: widgetCss,
            shopId: shopId!,
            settingType,
          })
        );
        break;
      case 'widgetfeatures':
        dispatch(
          postAdvancedSetting({
            value: widgetFeatures,
            shopId: shopId!,
            settingType,
          })
        );
        break;
    }
  };

  useEffect(() => {
    dispatch(fetchAdvancedSettings(shopId!));
  }, [dispatch, shopId]);
  
  useEffect(() => {
    if (editorHeight != ls.get('editor_height')) {
      ls.set('editor_height', editorHeight)
    }
  }, [editorHeight]);

  useEffect(() => {
    if (status === 'successful') {
      setCustomCss(data?.customCss ?? '');
      setCustomJs(data?.customJs ?? '');
      try {
        setWidgetCss(data?.widgetCss ? JSON.parse(data?.widgetCss) : '');
      } catch (_) {
        setWidgetCss(data?.widgetCss ? data?.widgetCss : '');
      }
      try {
        if (data) {
          const features = data?.widgetFeatures;
          JSON.parse(features);
          setWidgetFeatures(features);
        }
      } catch (_) {
        setWidgetFeatures(
          data?.widgetFeatures ? JSON.stringify(data?.widgetFeatures) : '{}'
        );
      }
    }
  }, [data, status]);

  const renderCustomCssEditor = () => (
    <div>
      <Title>Custom css</Title>
      <SubTitle>Advanced css for the CTA Button</SubTitle>
      <EditorWrapper style={{ height: editorHeight }}>
        <Editor
          value={customCss}
          height={'100%'}
          width={'100%'}
          language={'css'}
          options={{ minimap: { enabled: false } }}
          onChange={(value) => setCustomCss(value ?? '')}
        />
      </EditorWrapper>
      <Spacer height={12} />
      <s-button
        disabled={!customCss}
        onClick={() => handleSubmit('customcss')}
        variant='primary'
      >
        Update custom css
      </s-button>
    </div>
  );

  const renderCustomJsEditor = () => (
    <div>
      <Title>Custom js</Title>
      <SubTitle>Advanced Js for the store's integration</SubTitle>
      <EditorWrapper style={{ height: editorHeight }}>
        <Editor
          value={customJs}
          height={'100%'}
          width={'100%'}
          language={'javascript'}
          options={{ minimap: { enabled: false } }}
          onChange={(value) => setCustomJs(value ?? '')}
        />
      </EditorWrapper>
      <Spacer height={12} />
      <s-button
        disabled={!customJs}
        onClick={() => handleSubmit('customjs')}
        variant='primary'
      >
        Update custom js
      </s-button>
    </div>
  );

  const renderWidgetCssEditor = () => (
    <div>
      <Title>Widget css</Title>
      <SubTitle>Advanced css for the Widget</SubTitle>
      <EditorWrapper style={{ height: editorHeight }}>
        <Editor
          value={widgetCss}
          height={'100%'}
          width={'100%'}
          language={'css'}
          options={{ minimap: { enabled: false } }}
          onChange={(value) => setWidgetCss(value ?? '')}
        />
      </EditorWrapper>
      <Spacer height={12} />
      <s-button
        disabled={!widgetCss}
        onClick={() => handleSubmit('widgetcss')}
        variant='primary'
      >
        Update widget css
      </s-button>
    </div>
  );

  const renderWidgetFeaturesEditor = () => (
    <div>
      <Title>Shop features</Title>
      <SubTitle>Advanced configuration for shop features</SubTitle>
      <EditorWrapper style={{ height: editorHeight }}>
        <Editor
          value={widgetFeatures}
          height={'100%'}
          width={'100%'}
          language={'json'}
          options={{ minimap: { enabled: false } }}
          onChange={(value) => setWidgetFeatures(value ?? '')}
        />
      </EditorWrapper>
      <Spacer height={12} />
      <s-button
        disabled={!widgetFeatures}
        onClick={() => handleSubmit('widgetfeatures')}
        variant='primary'
      >
        Update shop features
      </s-button>
    </div>
  );

  const renderView = () => {
    if (status === 'loading') {
      return <Loading loadingMessage='Loading advanced settings' />;
    }
    return (
      <PageWrapper>
        {renderCustomCssEditor()}
        {renderCustomJsEditor()}
        {renderWidgetCssEditor()}
        {renderWidgetFeaturesEditor()}
      </PageWrapper>
    );
  };

  return (
    <>
      <PageWrapper style={{ display: 'flex', gap: '0', flexDirection: 'column' }}>
        <SubTitle>Scripts</SubTitle>
        <s-button-group>
          <s-button slot='secondary-actions' href={`${window.location.origin}/api/scripts/shopify/monthly_orders/${shopId}`} target='_blank'>Get Monthly Orders (last 12 months)</s-button>
        </s-button-group>
      </PageWrapper>

      <hr/>

      <PageWrapper style={{ display: 'flex', gap: '0', flexDirection: 'column' }}>
        <SubTitle>Editor height</SubTitle>
        <s-button-group>
          <s-button slot='secondary-actions' onClick={() => { setEditorHeight(320) }}>320</s-button>
          <s-button slot='secondary-actions' onClick={() => { setEditorHeight(640) }}>640</s-button>
          <s-button slot='secondary-actions' onClick={() => { setEditorHeight(800) }}>800</s-button>
          <s-button slot='secondary-actions' onClick={() => { setEditorHeight(1024) }}>1024</s-button>
        </s-button-group>
      </PageWrapper>

      <hr/>

      {renderView()}
    </>
  );
};

export default AdvancedSettings;

const PageWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 40px;
  padding: 24px;
  > * { min-width: 0; }
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    padding: 12px;
  }
`;

const Title = styled.p`
  font-size: 16px;
  font-weight: 600;
`;

const SubTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  font-color: #ccc;
`;

const EditorWrapper = styled.div`
  padding: 8px;
  box-shadow: 4px 8px 12px #d1d1d1;
  border-radius: 8px;
  height: ${ls.get('editor_height') || 320}px;
  width: 100%;
`;